import { MapPin, ExternalLink, Phone, Clock } from "lucide-react";
import { BRAND } from "../mock";
import { openExternal } from "../lib/openExternal";

export default function StudioMap() {
  const loc = BRAND.locations[0]; // Mississauga primary
  const addressQ = encodeURIComponent(loc.full);
  const embedSrc = `https://www.google.com/maps?q=${addressQ}&output=embed&z=15`;
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${addressQ}`;

  return (
    <section
      id="studio-map"
      data-testid="studio-map-section"
      className="relative py-24 md:py-28 bg-[#0c0119] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-4 font-label">
           Visit the Studio
          </div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1]">
            Mississauga's <span className="text-jester">most-reviewed</span><br />
            ceramic coating studio.
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Map */}
          <div
            data-testid="studio-map-iframe-wrap"
            className="lg:col-span-2 relative rounded-2xl overflow-hidden border border-white/10 bg-jester-card aspect-[16/9] lg:aspect-auto lg:min-h-[460px]"
          >
            <iframe
              title={`${BRAND.name}. Mississauga ceramic coating studio map`}
              src={embedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              className="absolute inset-0 w-full h-full grayscale-[0.3] contrast-[1.05]"
            />
          </div>

          {/* Studio info card */}
          <div className="rounded-2xl border border-acid/30 bg-gradient-to-br from-jester-purple/15 to-[#120127] p-7 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-acid/15 border border-acid/30 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-acid" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-acid font-label">
                  Mississauga Studio
                </div>
                <div className="font-display text-xl mt-0.5">
                  {BRAND.name}
                </div>
              </div>
            </div>

            <div className="space-y-4 text-sm flex-1">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1 font-label">
                  Address
                </div>
                <div className="text-white/85 leading-relaxed">
                  {loc.street}<br />
                  {loc.city}, {loc.region} {loc.postal}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1 font-label flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Hours
                </div>
                {BRAND.hours.map((h, i) => (
                  <div key={i} className="text-white/85">
                    {h.day} · <span className="text-acid">{h.time}</span>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1 font-label flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> Phone
                </div>
                <a
                  href={`tel:${BRAND.phone.replace(/\D/g, "")}`}
                  data-testid="studio-map-phone"
                  className="text-white/90 hover:text-acid transition-colors"
                >
                  {BRAND.phone}
                </a>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <a
                href={directionsHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(directionsHref);
                }}
                data-testid="studio-map-directions-btn"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] text-xs uppercase transition-colors"
              >
                Get Directions <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                href={BRAND.socials.google}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(BRAND.socials.google);
                }}
                data-testid="studio-map-gmb-btn"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-white/15 text-white hover:border-acid hover:text-acid font-label tracking-[0.2em] text-xs uppercase transition-colors"
              >
                View on Google <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
